import * as Yup from 'yup';
import { Box, Stack } from '@mui/material';
import { Field, FieldProps, Formik, FormikHelpers } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    PoolButton,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalProps,
    PoolModalTitle,
    PoolTextfield,
} from '@synergy/components-next';
import { ReactElement } from 'react';
import { fields } from '@synergy/utils';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import { copyWishList } from '../../../store/whish-list-actions';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { wishListDetailSelector } from '../../../store/whish-list-selectors';

interface FormValues {
    name: string;
}

const fieldNames = fields<FormValues>();

interface WishListCopyModalProps extends Omit<PoolModalProps, 'onClose'> {
    onClose: () => void;
}

export const WishListCopyModal = (props: WishListCopyModalProps): ReactElement => {
    const { onClose, ...modalProps } = props;
    const wishList = useAppSelector(wishListDetailSelector);
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const wishListId = +paramId || 'default';
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();

    const handleSubmit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>): Promise<void> => {
        const newWishList = await dispatch(copyWishList(wishListId, values.name));

        if (newWishList?.id) {
            navigate(`/wish-list/${newWishList.id}`);
        }

        handleClose(formikHelpers);
    };

    const handleClose = (formikHelpers: FormikHelpers<FormValues>): void => {
        setTimeout(() => formikHelpers.resetForm(), 300);
        onClose();
    };

    const initialValues: FormValues = {
        name: intl.formatMessage({ id: 'app.wishlist.copy.name' }, { name: wishList?.name ?? '' }),
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(),
    });

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize={true}
        >
            {formikProps => (
                <PoolModal {...modalProps} onClose={onClose}>
                    <PoolModalTitle>
                        <FormattedMessage id="app.wishList.copy.modalTitle" />
                    </PoolModalTitle>
                    <PoolModalContent sx={{ textAlign: 'start' }}>
                        <Stack gap={2} typography="body1">
                            <Box>
                                <Field name={fieldNames.name}>
                                    {({ field }: FieldProps<string>) => (
                                        <PoolTextfield
                                            fullWidth={true}
                                            required={true}
                                            label={<FormattedMessage id="app.wishList.add.label" />}
                                            {...field}
                                            autoFocus={true}
                                        />
                                    )}
                                </Field>
                            </Box>
                        </Stack>
                    </PoolModalContent>
                    <PoolModalActions>
                        <PoolButton variant="outlined" onClick={() => handleClose(formikProps)}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>
                        <PoolButton onClick={() => formikProps.handleSubmit()}>
                            <FormattedMessage id="app.wishList.copy" />
                        </PoolButton>
                    </PoolModalActions>
                </PoolModal>
            )}
        </Formik>
    );
};
